import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  setRef,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import AuthContext from "../../contexts/authContext";
import CloseReminder from "../../components/modals/CloseReminder";
import { Button } from "@mui/joy";
const Dashboard = () => {
  const { userData, setRefresh } = useContext(AuthContext);
  const [designId, setDesignId] = useState(null);
  const [openCloseReminder, setOpenCloseReminder] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    no_of_users: 0,
    no_of_customers: 0,
    payments_today: 0,
    payments_monthly: 0,
    schemes: 0,
    monthlyBreakdown: [],
  });

  const role =
    typeof window !== "undefined" ? localStorage.getItem("role") : "admin";

  // --------------------------------------------
  // ------------ MAIN EFFECT -------------------
  // --------------------------------------------

  useEffect(() => {
    setRefresh(true);
  }, []);

  useEffect(() => {
    if (!userData) return;
    if (!userData?.payments) return;

    const now = new Date();
    const todayStr = now.toDateString();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let payments_today = 0;
    let payments_monthly = 0;
    let monthlySums = {};

    // Determine payment source based on role
    let filteredPayments = userData.payments;

    if (role === "agent") {
      const agentId = localStorage.getItem("userId");

      filteredPayments = userData.payments.filter(
        (p) =>
          p?.createdBy === agentId ||
          p?.agentId._id === agentId ||
          p?.collectorId === agentId
      );
    }

    // Last 8 months structure
    for (let i = 7; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const key = `${date.toLocaleString("default", {
        month: "short",
      })} ${date.getFullYear()}`;
      monthlySums[key] = 0;
    }

    // Process payments
    filteredPayments.forEach((payment) => {
      const date = new Date(payment.createdAt);
      const amount = parseFloat(payment.amountPaid) || 0;

      if (date.toDateString() === todayStr) payments_today += amount;

      if (
        date.getMonth() === currentMonth &&
        date.getFullYear() === currentYear
      )
        payments_monthly += amount;

      const key = `${date.toLocaleString("default", {
        month: "short",
      })} ${date.getFullYear()}`;

      if (monthlySums[key] !== undefined) monthlySums[key] += amount;
    });

    setDashboardData({
      no_of_users:
        userData?.users?.filter((u) => u.status !== "erased").length || 0,
      no_of_customers:
        userData?.customers?.filter((c) => c.status !== "erased").length || 0,
      payments_today,
      payments_monthly,
      schemes:
        userData?.schemes?.filter((s) => s.status !== "erased").length || 0,
      monthlyBreakdown: Object.entries(monthlySums).map(
        ([month, payments]) => ({
          month,
          payments,
        })
      ),
    });
  }, [userData]);

  // --------------------------------------------
  // ------------ METRICS BY ROLE ---------------
  // --------------------------------------------
  const adminMetrics = [
    { label: "Users", value: dashboardData.no_of_users },
    { label: "Customers", value: dashboardData.no_of_customers },
    { label: "Schemes", value: dashboardData.schemes },
    { label: "Payments Today", value: `₹${dashboardData.payments_today}` },
    { label: "Current Month", value: `₹${dashboardData.payments_monthly}` },
  ];

  const employeeMetrics = [
    { label: "Schemes", value: dashboardData.schemes },
    { label: "Customers", value: dashboardData.no_of_customers },
    { label: "Payments Today", value: `₹${dashboardData.payments_today}` },
    { label: "Current Month", value: `₹${dashboardData.payments_monthly}` },
  ];

  const agentMetrics = [
    { label: "Customers", value: dashboardData.no_of_customers },
    { label: "Schemes", value: dashboardData.schemes },
    { label: "Payments Today", value: `₹${dashboardData.payments_today}` },
  ];

  const metrics =
    role === "admin"
      ? adminMetrics
      : role === "employee"
      ? employeeMetrics
      : agentMetrics;

  // --------------------------------------------

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ color: "#800001", fontWeight: "bold", mb: 3 }}
      >
        Dashboard Overview
      </Typography>

      {/* METRIC CARDS */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 2,
          mb: 4,
        }}
      >
        {metrics.map((metric, index) => (
          <Card
            key={index}
            sx={{
              borderRadius: 3,
              backgroundColor: "#FDCD58",
              boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" align="center">
                {metric.label}
              </Typography>
              <Typography
                variant="h5"
                align="center"
                sx={{ color: "#800001", fontWeight: "bold" }}
              >
                {metric.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ADMIN CHART + REMINDER SECTION */}
      {role === "admin" && (
        <Box
          sx={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
          }}
        >
          {/* PAYMENTS CHART */}
          <Paper
            sx={{
              flex: 2,
              p: 3,
              borderRadius: 3,
              border: "2px solid #FDCD58",
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 2, color: "#800001", fontWeight: "bold" }}
            >
              Monthly Payments (Last 8 Months)
            </Typography>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dashboardData.monthlyBreakdown}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(v) => `₹${v}`} />
                <Bar dataKey="payments" fill="#800001" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* UPCOMING REMINDERS */}
          <Box
            sx={{ flex: 1, maxWidth: 350, height: "400px", overflowY: "auto" }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 2, color: "#800001", fontWeight: "bold" }}
            >
              Upcoming Design Reminders
            </Typography>

            {userData?.assets
              ?.flatMap((asset) =>
                asset.designs?.map((d) => ({
                  ...d,
                  companyName: asset.companyName,
                }))
              )
              ?.sort(
                (a, b) =>
                  new Date(a.nextReminderDate) - new Date(b.nextReminderDate)
              )
              .map((design) => {
                const isToday =
                  new Date(design.nextReminderDate).setHours(0, 0, 0, 0) ===
                  new Date().setHours(0, 0, 0, 0);
                return (
                  <Paper
                    key={design._id}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: isToday ? "#FFCCCC" : "#FDCD58", // 🔥 Light red highlight
                      borderLeft: `5px solid ${
                        isToday ? "#800001" : "#800001"
                      }`,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography sx={{ fontWeight: "bold" }}>
                        {design.companyName}
                      </Typography>
                      <Button
                        sx={{
                          backgroundColor: "transparent",
                          color: "#800001",
                          textDecoration: "underline",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#550000",
                          },
                        }}
                        onClick={() => {
                          setDesignId(design._id);
                          setOpenCloseReminder(true);
                        }}
                      >
                        Close
                      </Button>
                    </Box>
                    <Typography>{design.designName}</Typography>
                    <Typography
                      sx={{ textAlign: "right", color: "#800001", mt: 1 }}
                    >
                      {new Date(design.nextReminderDate).toLocaleDateString(
                        "en-GB",
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        }
                      )}
                    </Typography>
                  </Paper>
                );
              })}
          </Box>
        </Box>
      )}
      <CloseReminder
        openCloseReminder={openCloseReminder}
        setOpenCloseReminder={setOpenCloseReminder}
        designId={designId}
      />
    </Box>
  );
};

export default Dashboard;
